<template>
  <Teleport to="body">
    <div v-if="visible" class="user-profile-dialog">
    <div class="dialog-overlay" @click="closeDialog"></div>
    <div class="dialog-content">
      <button class="close-button" @click="closeDialog">
        <i class="fas fa-times"></i>
      </button>

      <div class="container">
        <div class="header">
          <h1><i class="fas fa-user"></i> 个人设置</h1>
          <p class="subtitle">管理你的个人资料和账号绑定信息</p>
        </div>

        <!-- 个人信息卡片 -->
        <div class="section">
          <h2 class="section-title"><i class="fas fa-id-card"></i> 个人信息</h2>
          <p class="section-desc">你可以在下方更改你的个人资料信息</p>


          <!-- 昵称输入框 -->
          <div class="form-group">
            <label class="form-label">昵称</label>
            <el-input v-model="userInfo.nickname" :placeholder="$t('manga.userProfile.nicknamePlaceholder')" class="custom-el-input" />
          </div>

          <!-- 保存按钮 -->
          <el-button @click="saveProfile" type="primary" class="btn-primary">
            <i class="fas fa-save"></i> 保存设置
          </el-button>
        </div>

        <!-- 绑定账号卡片 -->
        <div class="section">
          <h2 class="section-title"><i class="fas fa-link"></i> 绑定账号</h2>
          <p class="section-desc">你可以在下方绑定或更换账号</p>

          <!-- 绑定手机 -->
          <!-- <div class="binding-item">
            <div class="binding-info">
              <div class="binding-icon phone">
                <i class="fas fa-mobile-alt"></i>
              </div>
              <div class="binding-details">
                <p class="binding-title">绑定手机</p>
                <p class="binding-status">{{ userInfo.phone ? $t('manga.userProfile.phoneBound', { phone: userInfo.phone }) : $t('manga.userProfile.phoneUnbound') }}</p>
              </div>
              <el-button size="small" type="primary" plain>
                {{ userInfo.phone ? $t('manga.userProfile.change') : $t('manga.userProfile.bind') }}
            </el-button>
          </div> -->

          <!-- 绑定邮箱 -->
          <div class="binding-item">
            <div class="binding-info">
              <div class="binding-icon email">
                <i class="fas fa-envelope"></i>
              </div>
              <div class="binding-details">
                <p class="binding-title">绑定邮箱</p>
                <p class="binding-status">{{ userInfo.email ? $t('manga.userProfile.emailBound', { email: userInfo.email }) : $t('manga.userProfile.emailUnbound') }}</p>
              </div>
              </div>
              <el-button size="small" type="primary" plain>
                {{ userInfo.email ? $t('manga.userProfile.change') : $t('manga.userProfile.bind') }}
            </el-button>
          </div>

          <!-- 绑定微信 -->
          <!-- <div class="binding-item">
            <div class="binding-info">
              <div class="binding-icon wechat">
                <i class="fab fa-weixin"></i>
              </div>
              <div class="binding-details">
                <p class="binding-title">绑定微信</p>
                <p class="binding-status">{{ userInfo.wechat ? $t('manga.userProfile.wechatBound') : $t('manga.userProfile.wechatUnbound') }}</p>
              </div>
              <el-button size="small" type="primary" plain>
                {{ userInfo.wechat ? $t('manga.userProfile.unbind') : $t('manga.userProfile.bind') }}
            </el-button>
          </div> -->
        </div>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { uploadAvatar, updateUserInfo } from '@/api/user/userInfo.js'

const { t } = useI18n()

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  userInfo: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:visible', 'save'])

const userInfo = ref({
  nickname: '',
  id: '',
  avatar: '',
  phone: '',
  email: '',
  wechat: false,
  registerTime: '',
  ...props.userInfo
})

const isUploading = ref(false)
const fileInput = ref(null)

watch(() => props.userInfo, (newVal) => {
  userInfo.value = { ...userInfo.value, ...newVal }
}, { deep: true })

const closeDialog = () => {
  emit('update:visible', false)
}

// 触发文件选择
const triggerFileInput = () => {
  fileInput.value?.click()
}

// 处理头像选择
const handleAvatarChange = (event) => {
  const file = event.target.files[0]
  if (!file) return

  // 验证文件类型
  if (!file.type.startsWith('image/')) {
      ElMessage.error(t('manga.userProfile.selectImageError'))
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      ElMessage.error(t('manga.userProfile.imageSizeError'))
    return
  }

  handleUploadAvatar(file)
}

// 上传头像
const handleUploadAvatar = async (file) => {
  isUploading.value = true

  try {
    const result = await uploadAvatar(file)

    if (result.code === 200 && result.data) {
      // 更新头像URL
      userInfo.value.avatar = result.data.url
      ElMessage.success(t('manga.userProfile.avatarSuccess'))
    } else {
      ElMessage.error(result.msg || t('manga.userProfile.avatarFailed'))
    }
  } catch (error) {
    console.error('upload avatar failed:', error)
    ElMessage.error(t('manga.userProfile.avatarRetry'))
  } finally {
    isUploading.value = false
    // 清空input，允许重复选择同一文件
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

// 保存个人信息
const saveProfile = async () => {
  try {
    const result = await updateUserInfo({
      id: userInfo.value.id,
      nickname: userInfo.value.nickname,
      avatar: userInfo.value.avatar
    })

    if (result.code === 200) {
      ElMessage.success(t('manga.userProfile.saveSuccess'))
    } else {
      ElMessage.error(result.msg || t('manga.userProfile.saveFailed'))
    }
  } catch (error) {
    console.error('save profile failed:', error)
    ElMessage.error(t('manga.userProfile.saveRetry'))
  }
}
</script>

<style scoped>
/* 添加Font Awesome */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

.user-profile-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.dialog-content {
  position: relative;
  max-width: 600px;
  max-height: 85vh;
  width: 100%;
  background: #1a1a2e;
  border: 1px solid rgba(94, 234, 212, 0.3);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(94, 234, 212, 0.1);
  border: 1px solid rgba(94, 234, 212, 0.3);
  color: #5eead4;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
  font-size: 14px;
}

.close-button:hover {
  background: rgba(94, 234, 212, 0.2);
  transform: scale(1.1);
}

.container {
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;
  max-height: 85vh;
  overflow-y: auto;
}

/* 自定义滚动条 */
.container::-webkit-scrollbar {
  width: 4px;
}

.container::-webkit-scrollbar-track {
  background: rgba(30, 41, 59, 0.3);
  border-radius: 2px;
}

.container::-webkit-scrollbar-thumb {
  background: rgba(94, 234, 212, 0.4);
  border-radius: 2px;
}

.container::-webkit-scrollbar-thumb:hover {
  background: rgba(94, 234, 212, 0.6);
}

.header {
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(94, 234, 212, 0.1);
}

.header h1 {
  color: #5eead4;
  font-size: 1.5rem;
  margin-bottom: 6px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.header .subtitle {
  color: #94a3b8;
  font-size: 0.9rem;
  margin: 0;
}

.section {
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 1.1rem;
  color: #f1f5f9;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.section-title i {
  color: #5eead4;
}

.section-desc {
  color: #94a3b8;
  font-size: 0.85rem;
  margin-bottom: 16px;
  margin-top: 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  color: #e2e8f0;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 6px;
}

.avatar-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-container {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(94, 234, 212, 0.1);
  border: 2px solid rgba(94, 234, 212, 0.3);
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(94, 234, 212, 0.1);
  color: #5eead4;
  font-size: 24px;
}

/* Element Plus 组件样式覆盖 */
.custom-el-input {
  width: 100%;
}

:deep(.custom-el-input .el-input__wrapper) {
  background: rgba(15, 23, 42, 0.8) !important;
  border: 1px solid rgba(94, 234, 212, 0.2) !important;
  box-shadow: none !important;
  border-radius: 6px;
  padding: 0 12px;
  height: 36px;
}

:deep(.custom-el-input .el-input__inner) {
  color: #f1f5f9 !important;
  font-size: 14px;
  height: 34px;
  line-height: 34px;
  background: rgba(94, 234, 212, 0.1) !important;
  border-color: rgba(94, 234, 212, 0.1) !important;
}

:deep(.custom-el-input .el-input__inner)::placeholder {
  color: #64748b !important;
}

:deep(.custom-el-input.disabled .el-input__wrapper) {
  background: rgba(15, 23, 42, 0.4) !important;
  border-color: rgba(94, 234, 212, 0.1) !important;
}

:deep(.custom-el-input.disabled .el-input__inner) {
  color: #64748b !important;
  cursor: not-allowed;
}

:deep(.custom-el-input .el-input__wrapper.is-focus),
:deep(.custom-el-input .el-input__wrapper.is-active) {
  border-color: rgba(94, 234, 212, 0.6) !important;
  background: rgba(15, 23, 42, 0.9) !important;
  box-shadow: 0 0 0 2px rgba(94, 234, 212, 0.1) !important;
}

.btn-primary {
  background: linear-gradient(90deg, #5eead4, #8b5cf6);
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  height: 36px;
  padding: 0 20px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  justify-content: center;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(94, 234, 212, 0.3);
}

:deep(.btn-primary span) {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-secondary {
  background: rgba(94, 234, 212, 0.1);
  color: #5eead4;
  border: 1px solid rgba(94, 234, 212, 0.3);
  border-radius: 6px;
  font-size: 13px;
  height: 32px;
  padding: 0 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-secondary:hover {
  background: rgba(94, 234, 212, 0.2);
  border-color: rgba(94, 234, 212, 0.5);
}

.btn-outline {
  background: transparent;
  color: #94a3b8;
  border: 1px solid rgba(94, 234, 212, 0.3);
  border-radius: 16px;
  font-size: 13px;
  height: 32px;
  padding: 0 16px;
  transition: all 0.3s ease;
  min-width: 60px;
}

.btn-outline:hover {
  border-color: rgba(94, 234, 212, 0.6);
  color: #5eead4;
  background: rgba(94, 234, 212, 0.1);
}

.binding-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(94, 234, 212, 0.1);
}

.binding-item:last-child {
  border-bottom: none;
}

.binding-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.binding-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: white;
  flex-shrink: 0;
}

.binding-icon.phone {
  background: #f97316;
}

.binding-icon.email {
  background: #3b82f6;
}

.binding-icon.wechat {
  background: #22c55e;
}

.binding-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.binding-title {
  color: #f1f5f9;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.binding-status {
  color: #64748b;
  font-size: 12px;
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .user-profile-dialog {
    padding: 10px;
  }

  .container {
    padding: 16px;
  }

  .dialog-content {
    max-height: 90vh;
  }

  .avatar-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .avatar-container {
    width: 50px;
    height: 50px;
  }

  .binding-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 0;
  }

  .header h1 {
    font-size: 1.3rem;
  }

  .section {
    padding: 16px;
  }
}
</style>